/**
 * Noble Designs header behaviours (ported from the Symmetry 5.6 customised header).
 * - <nd-nav-menu>: desktop dropdowns for the extra menus rendered inside #pageheader
 *   (inverted header nav, split navigation). The stock <main-navigation> only handles #main-nav.
 * - <nd-header-scroll>: hide logo on scroll (desktop), "pinned" class for sticky interiors header,
 *   stable transparent-header offset while the logo is hidden.
 * - <nd-announcement-marquee>: sets the scroll speed of the continuous announcement ticker.
 * All are custom elements so they re-initialise when the theme editor re-renders a section.
 */
(() => {
  const HOVER_DELAY = 250;

  if (!customElements.get('nd-nav-menu')) {
    class NdNavMenu extends HTMLElement {
      connectedCallback() {
        this.openTimeout = -1;
        this.lastOpen = null;
        this.section = this.closest('.section-header');
        this.items = [...this.querySelectorAll('.navigation__tier-1 > .navigation__item--with-children')];

        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onNavHover = this.onNavHover.bind(this);

        this.items.forEach((item) => {
          item.addEventListener('mouseenter', this.onEnter);
          item.addEventListener('mouseleave', this.onLeave);
          const link = item.querySelector(':scope > .navigation__link');
          if (link) {
            link.addEventListener('keydown', this.onKeydown);
            link.addEventListener('click', this.onClick);
            link.addEventListener('touchstart', this.onTouchStart, { passive: true });
          }
        });
        this.addEventListener('mouseenter', this.onNavHover);
        this.addEventListener('mouseleave', this.onNavHover);
      }

      disconnectedCallback() {
        this.items.forEach((item) => {
          item.removeEventListener('mouseenter', this.onEnter);
          item.removeEventListener('mouseleave', this.onLeave);
          const link = item.querySelector(':scope > .navigation__link');
          if (link) {
            link.removeEventListener('keydown', this.onKeydown);
            link.removeEventListener('click', this.onClick);
            link.removeEventListener('touchstart', this.onTouchStart);
          }
        });
        this.removeEventListener('mouseenter', this.onNavHover);
        this.removeEventListener('mouseleave', this.onNavHover);
        if (this.section) {
          this.section.classList.remove('section-header--nav-hover', 'section-header--nav-open');
        }
      }

      onNavHover(evt) {
        if (this.section) {
          this.section.classList.toggle('section-header--nav-hover', evt.type === 'mouseenter');
        }
      }

      open(item) {
        clearTimeout(this.openTimeout);
        clearTimeout(item.ndCloseTimeout);
        const openItems = this.items.filter((el) => el !== item && el.classList.contains('navigation__item--show-children'));
        openItems.filter((el) => el !== this.lastOpen).forEach((el) => el.classList.remove('navigation__item--show-children'));
        this.lastOpen = item;
        const delay = openItems.length === 0 ? 0 : HOVER_DELAY;
        this.openTimeout = setTimeout(() => {
          this.items.forEach((el) => el.classList.toggle('navigation__item--show-children', el === item));
          if (this.section) this.section.classList.add('section-header--nav-open');
          if (window.theme && theme.manuallyLoadImages) theme.manuallyLoadImages(item);
        }, delay);
        item.ndOpenTimeout = this.openTimeout;
        const link = item.querySelector(':scope > .navigation__link');
        if (link) link.setAttribute('aria-expanded', 'true');
      }

      close(item, immediate) {
        clearTimeout(item.ndOpenTimeout);
        const doClose = () => {
          item.classList.remove('navigation__item--show-children');
          if (this.section && !this.items.some((el) => el.classList.contains('navigation__item--show-children'))) {
            this.section.classList.remove('section-header--nav-open');
          }
        };
        if (immediate) {
          doClose();
        } else {
          item.ndCloseTimeout = setTimeout(doClose, HOVER_DELAY);
        }
        const link = item.querySelector(':scope > .navigation__link');
        if (link) link.setAttribute('aria-expanded', 'false');
      }

      onEnter(evt) {
        this.open(evt.currentTarget);
      }

      onLeave(evt) {
        this.close(evt.currentTarget);
      }

      onKeydown(evt) {
        if (evt.key === 'Enter') {
          const item = evt.currentTarget.parentElement;
          if (item.classList.contains('navigation__item--show-children')) {
            this.close(item, true);
          } else {
            this.open(item);
          }
          evt.preventDefault();
        } else if (evt.key === 'Escape') {
          this.close(evt.currentTarget.parentElement, true);
        }
      }

      onTouchStart(evt) {
        evt.currentTarget.ndTouchedAt = Date.now();
      }

      onClick(evt) {
        const link = evt.currentTarget;
        const item = link.parentElement;
        // first tap on a touch device opens the dropdown instead of following the link
        const touched = link.ndTouchedAt && Date.now() - link.ndTouchedAt < 1000;
        if ((touched && !item.classList.contains('navigation__item--show-children')) || link.getAttribute('href') === '#') {
          evt.preventDefault();
          this.items.filter((el) => el !== item).forEach((el) => this.close(el, true));
          this.open(item);
        }
      }
    }
    customElements.define('nd-nav-menu', NdNavMenu);
  }

  /* Desktop: the dropdown chevron beside top level links opens the dropdown (5.6 behaviour) */
  if (!window.ndHeaderToggleBound) {
    window.ndHeaderToggleBound = true;
    document.addEventListener('click', (evt) => {
      const toggle = evt.target.closest('.section-header .navigation__tier-1 > .navigation__item > .navigation__children-toggle');
      if (!toggle || window.innerWidth < 768) return;
      evt.preventDefault();
      const item = toggle.parentElement;
      item.dispatchEvent(new Event(item.classList.contains('navigation__item--show-children') ? 'mouseleave' : 'mouseenter'));
    });
  }

  if (!customElements.get('nd-header-scroll')) {
    class NdHeaderScroll extends HTMLElement {
      connectedCallback() {
        this.header = this.closest('page-header') ? this.closest('page-header').querySelector('.pageheader') : null;
        this.section = this.closest('.section-header');
        if (!this.header || !this.section) return;

        this.hideLogo = this.hasAttribute('data-hide-logo');
        this.transparent = this.hasAttribute('data-transparent');

        if (this.hideLogo) {
          this.onScroll = this.update.bind(this);
          this.onResize = () => {
            this.update();
            this.storeTransparentHeight();
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onResize);
          this.storeTransparentHeight();
          this.update();
        }

        if (this.hasAttribute('data-pinned') && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(([entry]) => {
            entry.target.classList.toggle('section-header-pinned', entry.intersectionRatio < 1);
          }, { rootMargin: '-1px 0px 220px 0px', threshold: [1] });
          this.observer.observe(this.section);
        }
      }

      disconnectedCallback() {
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.observer) this.observer.disconnect();
        document.documentElement.style.removeProperty('--nd-transparent-header-height');
      }

      /* Keep the transparent overlap equal to the full header height, so content
         does not jump when the logo area collapses. */
      storeTransparentHeight() {
        if (!this.transparent || this.header.classList.contains('pageheader--logo-hidden')) return;
        document.documentElement.style.setProperty('--nd-transparent-header-height', `${Math.ceil(this.section.clientHeight)}px`);
      }

      update() {
        if (window.innerWidth < 768) {
          this.header.classList.remove('pageheader--logo-hidden');
          return;
        }
        const bar = document.querySelector('.section-announcement-bar');
        const threshold = bar ? bar.offsetTop + bar.clientHeight : 0;
        const isHidden = this.header.classList.contains('pageheader--logo-hidden');
        if (!isHidden && window.scrollY > threshold + 10) {
          this.storeTransparentHeight();
          this.header.classList.add('pageheader--logo-hidden');
        } else if (isHidden && window.scrollY <= threshold) {
          this.header.classList.remove('pageheader--logo-hidden');
        }
      }
    }
    customElements.define('nd-header-scroll', NdHeaderScroll);
  }

  if (!customElements.get('nd-announcement-marquee')) {
    class NdAnnouncementMarquee extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.nd-annbar-marquee__track');
        if (!this.track) return;
        this.setSpeed = this.setSpeed.bind(this);
        this.setSpeed();
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.setSpeed);
          this.resizeObserver.observe(this.track);
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      /* ~60px per second, matching the 5.6 Splide AutoScroll speed of 1px per frame */
      setSpeed() {
        const distance = this.track.scrollWidth / 2;
        const speed = parseFloat(this.dataset.speed || '60');
        this.style.setProperty('--nd-marquee-duration', `${Math.max(distance / speed, 5)}s`);
      }
    }
    customElements.define('nd-announcement-marquee', NdAnnouncementMarquee);
  }
})();
